import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../CRMProfileScreen.css';
import { crmService } from '../../services/crmService';
import {
    CRMTabHero,
    CRMSectionHeader,
    CRMHeaderLinkButton,
    CRMHeaderDivider,
    TabIcons,
} from './CRMTabUI';

// Partner Type classification for Dependencies/Linked Partners.
// PM definitions (Apr 2026):
//   - Entity: direct/main partner relationship (default)
//   - Sister company: shares a common parent but remains independent
//   - Representative: independent entity that represents the partner
const PARTNER_TYPE_OPTIONS = ['Entity', 'Sister company', 'Representative'];
const DEFAULT_PARTNER_TYPE = 'Entity';

const PARTNER_TYPE_DESCRIPTIONS = {
    'Entity': 'Direct partner relationship',
    'Sister company': 'Shares a common parent, operates independently',
    'Representative': 'Independent entity that represents the partner',
};

// Curated default service catalogue. Sourced from the original component so
// behaviour stays identical; just rendered as pills instead of a dropdown.
const DEFAULT_SERVICE_CATALOGUE = [
    'Commodity Trading',
    'Shipping & Logistics',
    'Freight Forwarding',
    'Mining Operations',
    'Port Services',
    'Shipping Insurance',
    'Trade Finance',
    'Banking Services',
    'Warehousing & Storage',
    'Quality Inspection',
    'Vessel Chartering',
    'Customs & Clearance',
    'Commodity Financing',
    'Marine Insurance',
    'Bunker Supply',
    'Ship Agency Services',
    'Stevedoring',
    'Documentation Services',
    'Risk Management',
    'Compliance Services',
];

// Coerce mixed-shape input (legacy strings or new objects) into a canonical
// list of { name, type, partner_id, shared_group_credit, tri_party_contract }
// objects. Used both on initial load and whenever the parent profile updates
// so the UI never has to deal with the legacy shape.
//
// CRITICAL: partner_id, shared_group_credit and tri_party_contract MUST be
// preserved here. The backend serializer round-trips these keys; dropping one
// on normalize would silently reset that flag (and lose the resolved id) on
// every load/save.
export const normalizeDependencyEntries = (entries) => {
    if (!Array.isArray(entries)) return [];
    const seen = new Set();
    const result = [];
    for (const entry of entries) {
        let name = '';
        let type = DEFAULT_PARTNER_TYPE;
        let partnerId = null;
        let sharedGroupCredit = false;
        let triPartyContract = false;
        if (typeof entry === 'string') {
            name = entry.trim();
        } else if (entry && typeof entry === 'object') {
            name = typeof entry.name === 'string' ? entry.name.trim() : '';
            if (PARTNER_TYPE_OPTIONS.includes(entry.type)) {
                type = entry.type;
            }
            if (entry.partner_id !== undefined && entry.partner_id !== null) {
                partnerId = entry.partner_id;
            }
            sharedGroupCredit = Boolean(entry.shared_group_credit);
            triPartyContract = Boolean(entry.tri_party_contract);
        }
        if (!name) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({
            name,
            type,
            partner_id: partnerId,
            shared_group_credit: sharedGroupCredit,
            tri_party_contract: triPartyContract,
        });
    }
    return result;
};

const CRMPartnerServicesTab = ({ profileData, onDataChange, isNewProfile, isCreditApprover = false }) => {
    const [formData, setFormData] = useState(() => ({
        services_provided_selections: [],
        ...profileData,
        dependencies_linked_partners: normalizeDependencyEntries(profileData?.dependencies_linked_partners),
    }));

    // Dynamic dependencies from CRM database
    const [dependenciesOptions, setDependenciesOptions] = useState([]);
    const [filteredDependenciesOptions, setFilteredDependenciesOptions] = useState([]);
    const [loadingDependencies, setLoadingDependencies] = useState(false);

    // Search dropdown visibility for the dependencies search box
    const [showDependenciesDropdown, setShowDependenciesDropdown] = useState(false);
    const dependenciesRef = useRef(null);

    // Custom service inline form state
    const [showAddService, setShowAddService] = useState(false);
    const [newService, setNewService] = useState('');
    const newServiceInputRef = useRef(null);

    // Search functionality for dependencies dropdown
    const [dependenciesSearchTerm, setDependenciesSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const searchInputRef = useRef(null);

    // Close the dependencies search dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dependenciesRef.current && !dependenciesRef.current.contains(event.target)) {
                setShowDependenciesDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounce search term for dependencies
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(dependenciesSearchTerm);
        }, 250);
        return () => clearTimeout(timer);
    }, [dependenciesSearchTerm]);

    // Filter dependencies based on search term
    useEffect(() => {
        if (!debouncedSearchTerm.trim()) {
            setFilteredDependenciesOptions([]);
            return;
        }
        const lower = debouncedSearchTerm.toLowerCase();
        setFilteredDependenciesOptions(
            dependenciesOptions.filter(option => (option.name || '').toLowerCase().includes(lower))
        );
    }, [debouncedSearchTerm, dependenciesOptions]);

    // Fetch partner options ({id, name}) for dependencies dropdown. The id lets
    // us persist partner_id on each linked entry (see toggleDependency).
    useEffect(() => {
        const fetchPartnerOptions = async () => {
            try {
                setLoadingDependencies(true);
                const partnerOptions = await crmService.getPartnerOptions();
                setDependenciesOptions(partnerOptions);
                console.log(`Loaded ${partnerOptions.length} partner options for dependencies dropdown`);
            } catch (error) {
                console.error('Error fetching partner options for dependencies:', error);
                setDependenciesOptions([]);
            } finally {
                setLoadingDependencies(false);
            }
        };
        fetchPartnerOptions();
    }, []);

    useEffect(() => {
        if (profileData) {
            // CRITICAL FIX: Only reset formData on initial load, not on profile updates
            // This prevents losing user selections when the profile is saved for the first time
            setFormData(prevFormData => {
                const isInitialLoad = !prevFormData || Object.keys(prevFormData).length === 0 ||
                                     (!prevFormData.legal_entity_name && isNewProfile);

                if (isInitialLoad) {
                    console.log('PartnerServicesTab: Initial load - setting form data from profileData');
                    return {
                        services_provided_selections: [],
                        ...profileData,
                        dependencies_linked_partners: normalizeDependencyEntries(profileData.dependencies_linked_partners),
                    };
                }

                console.log('PartnerServicesTab: Profile update - preserving existing selections');
                const incomingDeps = profileData.dependencies_linked_partners;
                const hasIncomingDeps = Array.isArray(incomingDeps) && incomingDeps.length > 0;

                return {
                    ...prevFormData,
                    ...profileData,
                    services_provided_selections:
                        profileData.services_provided_selections ||
                        prevFormData.services_provided_selections ||
                        [],
                    dependencies_linked_partners: hasIncomingDeps
                        ? normalizeDependencyEntries(incomingDeps)
                        : (prevFormData.dependencies_linked_partners || []),
                };
            });
        }
    }, [profileData]);

    const handleInputChange = (field, value) => {
        const updatedData = { ...formData, [field]: value };
        setFormData(updatedData);
        onDataChange(field, updatedData[field]);
    };

    // === Services helpers ===
    // Combine the curated catalogue with any custom services already saved on
    // the profile, so previously-added customs are still visible as toggleable
    // pills (and the user can deselect them).
    const allServiceOptions = useMemo(() => {
        const selected = formData.services_provided_selections || [];
        const merged = [...DEFAULT_SERVICE_CATALOGUE];
        selected.forEach(s => {
            if (s && !merged.includes(s)) merged.push(s);
        });
        return merged;
    }, [formData.services_provided_selections]);

    const toggleService = (service) => {
        const current = formData.services_provided_selections || [];
        const updated = current.includes(service)
            ? current.filter(s => s !== service)
            : [...current, service];
        handleInputChange('services_provided_selections', updated);
    };

    const selectAllServices = () => {
        handleInputChange('services_provided_selections', allServiceOptions);
    };

    const clearAllServices = () => {
        handleInputChange('services_provided_selections', []);
    };

    const addCustomService = () => {
        const trimmed = newService.trim();
        if (!trimmed) return;
        const current = formData.services_provided_selections || [];
        if (!current.includes(trimmed)) {
            handleInputChange('services_provided_selections', [...current, trimmed]);
        }
        setNewService('');
        setShowAddService(false);
    };

    // === Dependencies helpers ===
    // Stored as { name, type } objects so each linked partner carries its own
    // Partner Type classification (Entity / Sister company / Representative).
    const isDependencySelected = (partnerName) => {
        const selections = formData.dependencies_linked_partners || [];
        return selections.some(item => item?.name === partnerName);
    };

    // Accepts either a picker option object ({ id, name }) or a bare name
    // string. When the option carries an id we persist it as partner_id so the
    // backend doesn't have to resolve the link by name. shared_group_credit
    // defaults to false for every new link.
    const toggleDependency = (option) => {
        const partnerName = typeof option === 'string' ? option : option?.name;
        if (!partnerName) return;
        const partnerId = typeof option === 'object' && option?.id != null ? option.id : null;
        const currentSelections = formData.dependencies_linked_partners || [];
        const exists = currentSelections.some(item => item?.name === partnerName);
        const updated = exists
            ? currentSelections.filter(item => item?.name !== partnerName)
            : [
                ...currentSelections,
                {
                    name: partnerName,
                    type: DEFAULT_PARTNER_TYPE,
                    partner_id: partnerId,
                    shared_group_credit: false,
                    tri_party_contract: false,
                },
            ];
        handleInputChange('dependencies_linked_partners', updated);
    };

    const removeDependency = (partnerName) => {
        const currentSelections = formData.dependencies_linked_partners || [];
        const updated = currentSelections.filter(item => item?.name !== partnerName);
        handleInputChange('dependencies_linked_partners', updated);
    };

    const updateDependencyType = (partnerName, newType) => {
        if (!PARTNER_TYPE_OPTIONS.includes(newType)) return;
        const currentSelections = formData.dependencies_linked_partners || [];
        const updated = currentSelections.map(item =>
            item?.name === partnerName ? { ...item, type: newType } : item
        );
        handleInputChange('dependencies_linked_partners', updated);
    };

    // Toggle whether a linked partner's credit limit is pooled into this
    // partner's group (internal risk indicator). Spreads the existing entry so
    // name/type/partner_id are preserved.
    const updateDependencySharedCredit = (partnerName, value) => {
        const currentSelections = formData.dependencies_linked_partners || [];
        const updated = currentSelections.map(item =>
            item?.name === partnerName ? { ...item, shared_group_credit: Boolean(value) } : item
        );
        handleInputChange('dependencies_linked_partners', updated);
    };

    // Toggle whether a tri-party contract exists with a linked partner. Open
    // to any CRM editor (unlike Shared credit): it records that a contract is
    // in place, it does not move credit risk.
    const updateDependencyTriParty = (partnerName, value) => {
        const currentSelections = formData.dependencies_linked_partners || [];
        const updated = currentSelections.map(item =>
            item?.name === partnerName ? { ...item, tri_party_contract: Boolean(value) } : item
        );
        handleInputChange('dependencies_linked_partners', updated);
    };

    // Group selected partners by Partner Type so the user can see the shape of
    // the entity's relationship network at a glance (Entities first, then
    // Sister companies, then Representatives).
    const groupedDependencies = useMemo(() => {
        const groups = Object.fromEntries(PARTNER_TYPE_OPTIONS.map(t => [t, []]));
        (formData.dependencies_linked_partners || []).forEach(entry => {
            if (!entry?.name) return;
            const t = PARTNER_TYPE_OPTIONS.includes(entry.type) ? entry.type : DEFAULT_PARTNER_TYPE;
            groups[t].push(entry);
        });
        return groups;
    }, [formData.dependencies_linked_partners]);

    const servicesSelectedCount = formData.services_provided_selections?.length || 0;
    const dependenciesSelectedCount = formData.dependencies_linked_partners?.length || 0;
    const hasSearch = debouncedSearchTerm.trim().length > 0;
    const visibleSearchResults = filteredDependenciesOptions.slice(0, 50);

    return (
        <div className="crm-tab-panel crm-tab-modern crm-ps-tab">
            <CRMTabHero
                tone="blue"
                icon={<TabIcons.Services />}
                title="Partner Services & Linked Entities"
                description="Capture what this partner does for IMT and how it relates to other CRM partners."
            />

            {/* === Services Provided ============================================ */}
            <section className="crm-form-section crm-ps-section">
                <CRMSectionHeader
                    icon={<TabIcons.Services />}
                    iconTone="blue"
                    title="Services Provided"
                    count={servicesSelectedCount}
                    countLabel="selected"
                    actions={
                        <>
                            <CRMHeaderLinkButton onClick={selectAllServices}>
                                Select all
                            </CRMHeaderLinkButton>
                            <CRMHeaderDivider />
                            <CRMHeaderLinkButton
                                onClick={clearAllServices}
                                disabled={servicesSelectedCount === 0}
                            >
                                Clear
                            </CRMHeaderLinkButton>
                        </>
                    }
                    help="Tap any commodity-trading service this partner provides to IMT or facilitates in our operations."
                />

                <div className="crm-ps-pills" role="group" aria-label="Services provided by partner">
                    {allServiceOptions.map(service => {
                        const isSelected = formData.services_provided_selections?.includes(service);
                        const isCustom = !DEFAULT_SERVICE_CATALOGUE.includes(service);
                        return (
                            <button
                                key={service}
                                type="button"
                                className={`crm-ps-pill ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => toggleService(service)}
                                aria-pressed={isSelected}
                            >
                                <span className="crm-ps-pill-label">{service}</span>
                                {isCustom && <span className="crm-ps-pill-badge">custom</span>}
                            </button>
                        );
                    })}

                    {!showAddService && (
                        <button
                            type="button"
                            className="crm-ps-pill crm-ps-pill-add"
                            onClick={() => {
                                setShowAddService(true);
                                setNewService('');
                                setTimeout(() => newServiceInputRef.current?.focus(), 50);
                            }}
                        >
                            + Add custom service
                        </button>
                    )}
                </div>

                {showAddService && (
                    <div className="crm-ps-custom-form">
                        <input
                            ref={newServiceInputRef}
                            type="text"
                            className="crm-input crm-ps-custom-input"
                            placeholder="e.g. Crew Management, Bunker Surveying..."
                            value={newService}
                            onChange={(e) => setNewService(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addCustomService();
                                } else if (e.key === 'Escape') {
                                    setShowAddService(false);
                                    setNewService('');
                                }
                            }}
                            maxLength={100}
                        />
                        <div className="crm-ps-custom-actions">
                            <button
                                type="button"
                                className="crm-btn-primary"
                                onClick={addCustomService}
                                disabled={!newService.trim()}
                            >
                                Add service
                            </button>
                            <button
                                type="button"
                                className="crm-btn-ghost"
                                onClick={() => {
                                    setShowAddService(false);
                                    setNewService('');
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* === Dependencies / Linked Partners ============================== */}
            <section className="crm-form-section crm-ps-section">
                <CRMSectionHeader
                    icon={<TabIcons.Link />}
                    iconTone="indigo"
                    title="Dependencies & Linked Partners"
                    count={dependenciesSelectedCount}
                    countLabel="linked"
                />

                <div className="crm-ps-type-legend" role="list">
                    {PARTNER_TYPE_OPTIONS.map(typeKey => (
                        <div key={typeKey} className="crm-ps-type-legend-item" data-type={typeKey} role="listitem">
                            <span className="crm-ps-type-dot" aria-hidden="true" />
                            <span className="crm-ps-type-legend-text">
                                <strong>{typeKey}</strong>
                                <span className="crm-ps-type-legend-desc"> &mdash; {PARTNER_TYPE_DESCRIPTIONS[typeKey]}</span>
                            </span>
                        </div>
                    ))}
                </div>

                <div className="crm-ps-search-wrapper" ref={dependenciesRef}>
                    <div className={`crm-ps-search ${showDependenciesDropdown ? 'is-active' : ''}`}>
                        <span className="crm-ps-search-icon" aria-hidden="true">
                            <TabIcons.Search width="16" height="16" />
                        </span>
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="crm-ps-search-input"
                            placeholder={
                                loadingDependencies
                                    ? 'Loading partner names...'
                                    : 'Search CRM partners to link...'
                            }
                            value={dependenciesSearchTerm}
                            onChange={(e) => {
                                setDependenciesSearchTerm(e.target.value);
                                setShowDependenciesDropdown(true);
                            }}
                            onFocus={() => setShowDependenciesDropdown(true)}
                            disabled={loadingDependencies}
                            aria-label="Search CRM partners"
                        />
                        {dependenciesSearchTerm && (
                            <button
                                type="button"
                                className="crm-ps-search-clear"
                                onClick={() => {
                                    setDependenciesSearchTerm('');
                                    searchInputRef.current?.focus();
                                }}
                                aria-label="Clear search"
                            >
                                ×
                            </button>
                        )}
                    </div>

                    {showDependenciesDropdown && hasSearch && !loadingDependencies && (
                        <div className="crm-ps-search-results" role="listbox">
                            {visibleSearchResults.length > 0 ? (
                                <>
                                    {visibleSearchResults.map(option => {
                                        const selected = isDependencySelected(option.name);
                                        return (
                                            <button
                                                key={option.id ?? option.name}
                                                type="button"
                                                className={`crm-ps-search-result ${selected ? 'is-selected' : ''}`}
                                                onClick={() => toggleDependency(option)}
                                                role="option"
                                                aria-selected={selected}
                                            >
                                                <span className="crm-ps-search-result-name">{option.name}</span>
                                                <span className="crm-ps-search-result-action">
                                                    {selected ? 'Linked' : '+ Link'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                    {filteredDependenciesOptions.length > visibleSearchResults.length && (
                                        <div className="crm-ps-search-more">
                                            Showing first {visibleSearchResults.length} of {filteredDependenciesOptions.length}. Refine your search to see more.
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="crm-ps-search-empty">
                                    No partners found matching &ldquo;{debouncedSearchTerm}&rdquo;.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {dependenciesSelectedCount > 0 ? (
                    <div className="crm-ps-partners">
                        <p className="crm-ps-shared-credit-hint">
                            Toggle <strong>Shared credit</strong> to pool a linked partner&rsquo;s
                            credit limit across the group &mdash; an internal risk indicator only
                            (invoices stay per company). Toggle{' '}
                            <strong className="tri">Tri-party contract</strong> to mark that a
                            tri-party contract is in place with that partner.
                        </p>
                        {PARTNER_TYPE_OPTIONS.map(typeKey => {
                            const partnersOfType = groupedDependencies[typeKey];
                            if (!partnersOfType || partnersOfType.length === 0) return null;
                            return (
                                <div key={typeKey} className="crm-ps-partner-group" data-type={typeKey}>
                                    <div className="crm-ps-group-header">
                                        <span className="crm-ps-type-dot" aria-hidden="true" />
                                        <span className="crm-ps-group-name">{typeKey}</span>
                                        <span className="crm-ps-group-count">{partnersOfType.length}</span>
                                    </div>
                                    <ul className="crm-ps-partner-list">
                                        {partnersOfType.map(partner => (
                                            <li key={partner.name} className="crm-ps-partner-row" data-type={typeKey}>
                                                <span className="crm-ps-partner-name" title={partner.name}>
                                                    {partner.name}
                                                </span>
                                                <select
                                                    className="crm-ps-partner-type-select"
                                                    value={partner.type}
                                                    onChange={(e) => updateDependencyType(partner.name, e.target.value)}
                                                    aria-label={`Partner type for ${partner.name}`}
                                                    title="Change Partner Type"
                                                >
                                                    {PARTNER_TYPE_OPTIONS.map(opt => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                                <label
                                                    className={`crm-ps-shared-credit ${partner.shared_group_credit ? 'is-on' : ''}`}
                                                    title={
                                                        isCreditApprover
                                                            ? 'Pool credit limit across the group'
                                                            : 'Only Credit Approvers can change this'
                                                    }
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="crm-ps-shared-credit-checkbox"
                                                        checked={Boolean(partner.shared_group_credit)}
                                                        onChange={(e) =>
                                                            updateDependencySharedCredit(partner.name, e.target.checked)
                                                        }
                                                        disabled={!isCreditApprover}
                                                        aria-label={`Shared group credit for ${partner.name}`}
                                                    />
                                                    <span className="crm-ps-shared-credit-text">Shared credit</span>
                                                </label>
                                                <label
                                                    className={`crm-ps-shared-credit crm-ps-tri-party ${partner.tri_party_contract ? 'is-on' : ''}`}
                                                    title="Mark that a tri-party contract is in place with this partner"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="crm-ps-shared-credit-checkbox"
                                                        checked={Boolean(partner.tri_party_contract)}
                                                        onChange={(e) =>
                                                            updateDependencyTriParty(partner.name, e.target.checked)
                                                        }
                                                        aria-label={`Tri-party contract for ${partner.name}`}
                                                    />
                                                    <span className="crm-ps-shared-credit-text">Tri-party contract</span>
                                                </label>
                                                <button
                                                    type="button"
                                                    className="crm-ps-partner-remove"
                                                    onClick={() => removeDependency(partner.name)}
                                                    aria-label={`Unlink ${partner.name}`}
                                                    title="Unlink partner"
                                                >
                                                    ×
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="crm-ps-empty">
                        <div className="crm-ps-empty-title">No partners linked yet</div>
                        <div className="crm-ps-empty-desc">
                            Use the search above to link CRM partners and classify each one&rsquo;s relationship type.
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default CRMPartnerServicesTab;
